import { useRef, useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { Box } from '@mantine/core';
import { Group, Panel, PanelImperativeHandle, PanelSize, Separator, usePanelRef } from 'react-resizable-panels';
import { useDiscoveryStore } from '../../stores/useDiscoveryStore';
import { CHAT_PANEL_MIN, CHAT_PANEL_MAX, INSIGHTS_PANEL_MIN } from '../../stores/useDiscoveryStore';
import { useActiveDiscoverySession } from '../../hooks/useDiscoveryAgent';
import { useFiltersContextHelper } from '../ideas/filters/useFiltersContextHelper';
import ExploreSessionList from './ExploreSessionList';
import ExploreMainContent from './ExploreMainContent';
import ExploreInsightsPanel from './ExploreInsightsPanel';
import { useIdeaFilters } from '../../hooks/useIdeaFilters';
import { enrichSessionFiltersObjectIdMaps } from './utils/buildSessionFilters';

const FULL_HEIGHT = '100vh';

export const ExplorePage = () => {
  const leftPanelRef = usePanelRef();
  const insightsPanelRef = useRef<PanelImperativeHandle | null>(null);
  const { ideasFiltersConfig } = useIdeaFilters();

  const { session, sessionId } = useActiveDiscoverySession();
  // defaultSize is initial-only: feeding it the live width re-registers the panel Group
  // on every resize, which freezes a drag already in progress (BKR-253).
  const [initialChatPanelWidth] = useState(() => useDiscoveryStore.getState().chatPanelWidth);
  const [initialInsightsPanelWidth] = useState(() => useDiscoveryStore.getState().insightsPanelWidth);
  const isInsightsPanelOpen = useDiscoveryStore((s) => s.isInsightsPanelOpen);
  const isSessionsOpen = useDiscoveryStore((s) => s.isSessionsOpen);
  const setChatPanelWidth = useDiscoveryStore((s) => s.setChatPanelWidth);
  const setInsightsPanelWidth = useDiscoveryStore((s) => s.setInsightsPanelWidth);
  const setSessionsOpen = useDiscoveryStore((s) => s.setSessionsOpen);
  const openInsights = useDiscoveryStore((s) => s.openInsights);
  const closeInsights = useDiscoveryStore((s) => s.closeInsights);
  const { setActiveFilters: setContextFilters, setCheckedComponents } = useFiltersContextHelper();

  const showInsightsPanel = isInsightsPanelOpen && sessionId != null;

  useLayoutEffect(() => {
    isSessionsOpen ? leftPanelRef.current?.expand() : leftPanelRef.current?.collapse();
  }, [isSessionsOpen]);

  useEffect(() => {
    showInsightsPanel
      ? insightsPanelRef.current?.expand()
      : insightsPanelRef.current?.collapse();
  }, [showInsightsPanel]);

  // When session or sessionFilters change (refresh, switch session, or optimistic update after PATCH),
  // hydrate store from session.sessionFilters, then push one-way to context for CheckboxFilter/ChildMenu.
  const prevFiltersKeyRef = useRef<string>('');
  useEffect(() => {
    const filtersKey = `${sessionId ?? ''}:${JSON.stringify(session?.sessionFilters ?? [])}`;
    if (prevFiltersKeyRef.current === filtersKey) return;
    prevFiltersKeyRef.current = filtersKey;

    const raw = session?.sessionFilters ?? [];
    const sessionFilters = enrichSessionFiltersObjectIdMaps(raw, ideasFiltersConfig);
    useDiscoveryStore.getState().setSessionFilters(sessionFilters);

    setContextFilters(sessionFilters);
    setCheckedComponents(
      Object.fromEntries(
        sessionFilters.map((f) => [f.parent, (f.selected ?? []).map(String)])
      )
    );
  }, [session, sessionId, ideasFiltersConfig, setContextFilters, setCheckedComponents]);

  const handleChatPanelResize = useCallback(
    ({ inPixels }: PanelSize) => {
      if (inPixels > 0 && inPixels < CHAT_PANEL_MIN && isSessionsOpen) {
        leftPanelRef.current?.resize(`${CHAT_PANEL_MIN}px`);
        return;
      }
      if (inPixels > 0) {
        setChatPanelWidth(Math.max(CHAT_PANEL_MIN, Math.min(CHAT_PANEL_MAX, inPixels)));
      }
    },
    [setChatPanelWidth, isSessionsOpen, leftPanelRef]
  );

  const handleInsightsPanelResize = useCallback(
    ({ inPixels }: PanelSize) => {
      if (inPixels > 0) {
        setInsightsPanelWidth(Math.max(INSIGHTS_PANEL_MIN, inPixels));
      }
    },
    [setInsightsPanelWidth]
  );

  const handleInsightsPanelClose = useCallback(() => {
    const shouldKeepSessionsOpen = isSessionsOpen;
    closeInsights();
    if (shouldKeepSessionsOpen) {
      setSessionsOpen(true);
    }
    insightsPanelRef.current?.collapse();
  }, [closeInsights, isSessionsOpen, setSessionsOpen]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: FULL_HEIGHT,
        minHeight: FULL_HEIGHT,
        backgroundColor: '#fff',
      }}
    >
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        <Group
          orientation="horizontal"
          id="explore-panels"
          style={{ flex: 1, minWidth: 0, minHeight: 0, height: '100%' }}
        >
          <Panel
            id="chat"
            panelRef={leftPanelRef}
            defaultSize={`${initialChatPanelWidth}px`}
            minSize={`${CHAT_PANEL_MIN}px`}
            maxSize={`${CHAT_PANEL_MAX}px`}
            collapsible
            collapsedSize="0px"
            onResize={handleChatPanelResize}
            style={{
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              transition: 'flex-basis 0.28s cubic-bezier(0.4, 0, 0.2, 1), width 0.28s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          >
            <ExploreSessionList
              isCollapsed={!isSessionsOpen}
              onCollapse={() => setSessionsOpen(false)}
              onExpand={() => setSessionsOpen(true)}
            />
          </Panel>

          <Separator id="explore-resize-chat-main" />

          <Panel
            id="main"
            minSize="320px"
            style={{
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              transition: 'flex-basis 0.28s cubic-bezier(0.4, 0, 0.2, 1), width 0.28s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          >
            <ExploreMainContent
              isLeftSidebarCollapsed={!isSessionsOpen}
              onExpandLeftSidebar={() => setSessionsOpen(true)}
              handleLeftPanelCollapse={openInsights}
            />
          </Panel>

          <Separator id="explore-resize-main-insights" />

          <Panel
            id="insights"
            panelRef={insightsPanelRef}
            defaultSize={`${initialInsightsPanelWidth}px`}
            minSize="35vw"
            maxSize="50vw"
            collapsible
            collapsedSize="0px"
            onResize={handleInsightsPanelResize}
            style={{
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              minWidth: 0,
              overflow: 'hidden',
              borderLeft: showInsightsPanel ? '1px solid #E9ECEF' : 'none',
              transition: 'flex-basis 0.28s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          >
            <ExploreInsightsPanel onClosePanel={handleInsightsPanelClose} />
          </Panel>
        </Group>
      </Box>
    </Box>
  );
}
